'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import CalendarStyleDatePicker from '@/components/shared/CalendarStyleDatePicker';
import { getTimePartsSAST } from '@/lib/sastTime';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';

const DATE_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_VALUE_REGEX = /^\d{2}:\d{2}$/;

function formatDateDraft(rawValue: string): string {
  const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 8);

  if (digitsOnly.length <= 4) {
    return digitsOnly;
  }

  if (digitsOnly.length <= 6) {
    return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4)}`;
  }

  return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 6)}-${digitsOnly.slice(6, 8)}`;
}

function formatTimeDraft(rawValue: string): string {
  const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 4);

  if (digitsOnly.length <= 2) {
    return digitsOnly;
  }

  return `${digitsOnly.slice(0, 2)}:${digitsOnly.slice(2, 4)}`;
}

function isValidDateValue(value: string): boolean {
  if (!DATE_VALUE_REGEX.test(value)) {
    return false;
  }

  const [yearValue, monthValue, dayValue] = value.split('-').map((segment) => Number.parseInt(segment, 10));
  const date = new Date(yearValue, monthValue - 1, dayValue);

  return (
    date.getFullYear() === yearValue &&
    date.getMonth() === monthValue - 1 &&
    date.getDate() === dayValue
  );
}

function isValidTimeValue(value: string): boolean {
  if (!TIME_VALUE_REGEX.test(value)) {
    return false;
  }

  const [hoursValue, minutesValue] = value.split(':').map((segment) => Number.parseInt(segment, 10));
  return hoursValue >= 0 && hoursValue <= 23 && minutesValue >= 0 && minutesValue <= 59;
}

function isDateWithinRange(value: string, min?: string, max?: string): boolean {
  if (!value) {
    return true;
  }

  if (min && value < min) {
    return false;
  }

  if (max && value > max) {
    return false;
  }

  return true;
}

function parseDateValueToLocalDate(value: string): Date {
  if (!DATE_VALUE_REGEX.test(value)) {
    return new Date();
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return new Date();
  }

  return date;
}

function getTimePartsFromValue(value: string): { hours: number; minutes: number } {
  const [hour = 9, minute = 0] = (value || '').split(':').map((s) => Number.parseInt(s, 10));
  return {
    hours: Number.isNaN(hour) || hour < 0 || hour > 23 ? 9 : hour,
    minutes: Number.isNaN(minute) || minute < 0 || minute > 59 ? 0 : minute,
  };
}

interface SharedPickerInputProps {
  label?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  isDark?: boolean;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
}

interface DatePickerInputProps extends SharedPickerInputProps {
  value: string;
  onChange: (nextValue: string) => void;
  min?: string;
  max?: string;
  primaryColor?: string;
}

interface TimePickerInputProps extends SharedPickerInputProps {
  value: string;
  onChange: (nextValue: string) => void;
}

function usePopoverPosition(ref: React.RefObject<HTMLElement | null>) {
  const [position, setPosition] = useState<{ top: number; left: number; width: number; openUp: boolean }>({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });

  const updatePosition = useCallback(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const minWidth = 240;
    const width = Math.max(rect.width, minWidth);
    const viewportWidth = uiViewportWidth();
    const viewportHeight = uiViewportHeight();

    const left = Math.min(Math.max(12, rect.left), Math.max(12, viewportWidth - width - 12));

    const dropdownHeight = 340;
    const spaceBelow = viewportHeight - rect.bottom;
    const openUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    const top = openUp ? Math.max(12, rect.top - dropdownHeight - 4) : rect.bottom + 4;

    setPosition({ top, left, width, openUp });
  }, [ref]);

  return { position, updatePosition };
}

function usePopoverOpenState(ref: React.RefObject<HTMLElement | null>, portalRef: React.RefObject<HTMLElement | null>) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && ref.current.contains(target)) return;
      if (portalRef.current && portalRef.current.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, ref, portalRef]);

  return { isOpen, setIsOpen };
}

// ============================================================================
// Date Picker Input
// ============================================================================

export function DatePickerInput({
  value,
  onChange,
  label,
  helperText,
  disabled = false,
  required = false,
  isDark = false,
  className,
  inputClassName,
  min,
  max,
  placeholder = 'YYYY-MM-DD',
  primaryColor = '#3b82f6',
}: DatePickerInputProps) {
  const [prevValue, setPrevValue] = useState(value || '');
  const [draftValue, setDraftValue] = useState(value || '');

  if (value !== prevValue) {
    setPrevValue(value || '');
    setDraftValue(value || '');
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const { isOpen, setIsOpen } = usePopoverOpenState(containerRef, portalRef);
  const { position, updatePosition } = usePopoverPosition(containerRef);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const statusToneClassName = useMemo(() => {
    if (!draftValue) {
      return isDark ? 'text-gray-400' : 'text-gray-500';
    }

    if (!isValidDateValue(draftValue) || !isDateWithinRange(draftValue, min, max)) {
      return isDark ? 'text-amber-300' : 'text-amber-700';
    }

    return isDark ? 'text-emerald-300' : 'text-emerald-700';
  }, [draftValue, isDark, max, min]);

  const baseInputClassName = cn(
    'w-full rounded-xl border px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2',
    isDark
      ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/30'
      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-200',
    disabled && 'cursor-not-allowed opacity-60',
    inputClassName,
  );

  const commitValue = (rawValue: string) => {
    const normalizedValue = formatDateDraft(rawValue || draftValue).slice(0, 10);

    if (!normalizedValue) {
      setDraftValue(value || '');
      return;
    }

    if (isValidDateValue(normalizedValue) && isDateWithinRange(normalizedValue, min, max)) {
      setDraftValue(normalizedValue);
      onChange(normalizedValue);
      return;
    }

    setDraftValue(value || '');
  };

  const selectedDate = useMemo(() => parseDateValueToLocalDate(value), [value]);
  const minDate = useMemo(() => (min ? parseDateValueToLocalDate(min) : undefined), [min]);
  const maxDate = useMemo(() => (max ? parseDateValueToLocalDate(max) : undefined), [max]);

  const handleSelectDate = (date: Date) => {
    // The calendar emits a local-midnight Date — format with local getters so
    // the picked day is returned verbatim in any timezone (getDateStrSAST
    // would land on the previous day for users east of Africa/Johannesburg).
    const nextValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (isValidDateValue(nextValue) && isDateWithinRange(nextValue, min, max)) {
      onChange(nextValue);
    }
    setIsOpen(false);
  };

  const pickerContent = isOpen && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={portalRef}
          className={cn(
            'fixed z-[9999] rounded-2xl border p-2 shadow-xl',
            isDark ? 'border-gray-700 bg-gray-900/95' : 'border-gray-200 bg-white',
          )}
          style={{ top: position.top, left: position.left, width: Math.max(position.width, 280) }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CalendarStyleDatePicker
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            primaryColor={primaryColor}
            isDark={isDark}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={className}>
      {label && (
        <label className={cn('mb-1 block text-xs font-medium', isDark ? 'text-gray-400' : 'text-gray-600')}>
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => !prev);
            }
          }}
          className={cn(
            'absolute left-0 top-0 flex h-full w-12 items-center justify-center rounded-l-xl transition-colors',
            isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
          aria-label="Open calendar"
          tabIndex={-1}
        >
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
        </button>
        <input
          type="text"
          value={draftValue}
          onChange={(event) => {
            const nextDraftValue = formatDateDraft(event.target.value);
            setDraftValue(nextDraftValue);

            if (isValidDateValue(nextDraftValue) && isDateWithinRange(nextDraftValue, min, max)) {
              onChange(nextDraftValue);
            }
          }}
          onBlur={(event) => commitValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitValue((event.target as HTMLInputElement).value);
            }
          }}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={Boolean(draftValue && (!isValidDateValue(draftValue) || !isDateWithinRange(draftValue, min, max)))}
          className={cn(baseInputClassName, 'pl-12 font-mono tracking-[0.04em]')}
          style={{ paddingLeft: '48px' }}
        />
      </div>
      {helperText && <p className={cn('mt-1 text-[11px]', statusToneClassName)}>{helperText}</p>}
      {pickerContent}
    </div>
  );
}

// ============================================================================
// Time Picker Input
// ============================================================================

export function TimePickerInput({
  value,
  onChange,
  label,
  helperText,
  disabled = false,
  required = false,
  isDark = false,
  className,
  inputClassName,
  placeholder = 'HH:MM',
}: TimePickerInputProps) {
  const [prevValue, setPrevValue] = useState(value || '');
  const [draftValue, setDraftValue] = useState(value || '');

  if (value !== prevValue) {
    setPrevValue(value || '');
    setDraftValue(value || '');
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const { isOpen, setIsOpen } = usePopoverOpenState(containerRef, portalRef);
  const { position, updatePosition } = usePopoverPosition(containerRef);

  const [mode, setMode] = useState<'hour' | 'minute'>('hour');

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const statusToneClassName = useMemo(() => {
    if (!draftValue) {
      return isDark ? 'text-gray-400' : 'text-gray-500';
    }

    return isValidTimeValue(draftValue)
      ? isDark ? 'text-emerald-300' : 'text-emerald-700'
      : isDark ? 'text-amber-300' : 'text-amber-700';
  }, [draftValue, isDark]);

  const baseInputClassName = cn(
    'w-full rounded-xl border px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2',
    isDark
      ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/30'
      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-200',
    disabled && 'cursor-not-allowed opacity-60',
    inputClassName,
  );

  const commitValue = (rawValue: string) => {
    const normalizedValue = formatTimeDraft(rawValue || draftValue).slice(0, 5);

    if (!normalizedValue) {
      setDraftValue(value || '');
      return;
    }

    if (isValidTimeValue(normalizedValue)) {
      setDraftValue(normalizedValue);
      onChange(normalizedValue);
      return;
    }

    setDraftValue(value || '');
  };

  const { hours: hour, minutes: minute } = getTimePartsFromValue(value);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const handleHourSelect = (h: number) => {
    const nextValue = `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onChange(nextValue);
    setMode('minute');
  };

  const handleMinuteSelect = (m: number) => {
    const nextValue = `${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange(nextValue);
    setIsOpen(false);
    setMode('hour');
  };

  const displayValue = value ? `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}` : '--:--';

  const pickerContent = isOpen && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={portalRef}
          className={cn(
            'fixed z-[9999] rounded-xl shadow-xl border overflow-hidden',
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          )}
          style={{ top: position.top, left: position.left, minWidth: '240px' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className={cn('flex border-b', isDark ? 'border-gray-700' : 'border-gray-200')}>
            <button
              type="button"
              onClick={() => setMode('hour')}
              className={cn(
                'flex-1 py-2 text-xs font-semibold tracking-wide uppercase transition-colors',
                mode === 'hour'
                  ? 'text-green-500 border-b-2 border-green-500'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              Hour
            </button>
            <button
              type="button"
              onClick={() => setMode('minute')}
              className={cn(
                'flex-1 py-2 text-xs font-semibold tracking-wide uppercase transition-colors',
                mode === 'minute'
                  ? 'text-green-500 border-b-2 border-green-500'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              Min
            </button>
          </div>

          <div className={cn('px-3 py-2 text-center border-b', isDark ? 'border-gray-700' : 'bg-gray-50 border-gray-200')}>
            <span className={cn('text-lg font-mono font-bold', isDark ? 'text-gray-100' : 'text-gray-800')}>
              <span className={mode === 'hour' ? 'text-green-500' : ''}>{String(hour).padStart(2, '0')}</span>
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>:</span>
              <span className={mode === 'minute' ? 'text-green-500' : ''}>{String(minute).padStart(2, '0')}</span>
            </span>
          </div>

          <div className="p-2">
            {mode === 'hour' ? (
              <div className="grid grid-cols-6 gap-1">
                {hours.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => handleHourSelect(h)}
                    className={cn(
                      'py-1.5 rounded-md text-sm font-medium transition-all',
                      h === hour
                        ? 'bg-green-500 text-white shadow-sm'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    {String(h).padStart(2, '0')}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {minutes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleMinuteSelect(m)}
                    className={cn(
                      'py-2 rounded-md text-sm font-medium transition-all',
                      m === minute
                        ? 'bg-green-500 text-white shadow-sm'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    :{String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={className}>
      {label && (
        <label className={cn('mb-1 block text-xs font-medium', isDark ? 'text-gray-400' : 'text-gray-600')}>
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          className={cn(
            'absolute left-0 top-0 flex h-full w-12 items-center justify-center rounded-l-xl transition-colors',
            isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
          aria-label="Open time picker"
          tabIndex={-1}
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => {
                const next = !prev;
                if (next) {
                  setMode('hour');
                  updatePosition();
                }
                return next;
              });
            }
          }}
        >
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
        </button>
        <input
          type="text"
          value={draftValue}
          onChange={(event) => {
            const nextDraftValue = formatTimeDraft(event.target.value);
            setDraftValue(nextDraftValue);

            if (isValidTimeValue(nextDraftValue)) {
              onChange(nextDraftValue);
            }
          }}
          onBlur={(event) => commitValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitValue((event.target as HTMLInputElement).value);
            }
          }}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={Boolean(draftValue && !isValidTimeValue(draftValue))}
          className={cn(baseInputClassName, 'pl-12 font-mono tracking-[0.08em]')}
          style={{ paddingLeft: '48px' }}
        />
      </div>
      {helperText && <p className={cn('mt-1 text-[11px]', statusToneClassName)}>{helperText}</p>}
      {pickerContent}
    </div>
  );
}
